import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Upload, Download, Loader2, Plus, Trash2, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Papa from 'papaparse';

interface LocationData {
  cities: any[];
  districts: any[];
}

export default function BulkDistrictImport() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [locationData, setLocationData] = useState<LocationData>({
    cities: [],
    districts: []
  });
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [newDistricts, setNewDistricts] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const { data: cities, error } = await supabase
        .from('cities')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      setLocationData(prev => ({ ...prev, cities }));
    } catch (err) {
      console.error('Error loading cities:', err);
      setError('Şehirler yüklenirken bir hata oluştu.');
    }
  };

  const handleAddDistrict = () => {
    setNewDistricts(prev => [...prev, '']);
  };

  const handleRemoveDistrict = (index: number) => {
    setNewDistricts(prev => prev.filter((_, i) => i !== index));
  };

  const handleDistrictChange = (index: number, value: string) => {
    setNewDistricts(prev => prev.map((district, i) => i === index ? value : district));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedCity) {
      setError('Lütfen önce bir şehir seçin.');
      return;
    }

    Papa.parse(file, {
      complete: (results) => {
        const districts = results.data
          .filter((row: any) => row[0] && typeof row[0] === 'string')
          .map((row: any) => row[0].trim())
          .filter(Boolean);

        if (districts.length === 0) {
          setError('Geçerli ilçe adı bulunamadı.');
          return;
        }

        setNewDistricts(districts);
        setSuccess(`${districts.length} ilçe başarıyla yüklendi. Kaydetmek için "Kaydet" butonuna tıklayın.`);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        setError('CSV dosyası okunurken bir hata oluştu.');
      }
    });
  };

  const downloadTemplate = () => {
    const csv = 'İlçe Adı\nÖrnek İlçe 1\nÖrnek İlçe 2';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'ilce_sablonu.csv';
    link.click();
  };

  const handleSubmit = async () => {
    if (!selectedCity) {
      setError('Lütfen bir şehir seçin.');
      return;
    }

    const validDistricts = newDistricts.filter(district => district.trim() !== '');
    if (validDistricts.length === 0) {
      setError('En az bir ilçe adı girin.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error } = await supabase
        .from('districts')
        .insert(
          validDistricts.map(name => ({
            name: name.trim(),
            city_id: selectedCity
          }))
        )
        .select();

      if (error) throw error;

      setSuccess(`${validDistricts.length} ilçe başarıyla eklendi.`);
      setNewDistricts(['']);
      setSelectedCity('');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error saving districts:', err);
      setError('İlçeler kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Toplu İlçe Yükleme</h1>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 space-y-6">
        <div className="space-y-4">
          {/* Şehir Seçimi */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Şehir Seçin
            </label>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">Seçiniz</option>
              {locationData.cities.map(city => (
                <option key={city.id} value={city.id}>{city.name}</option>
              ))}
            </select>
          </div>

          {/* CSV Yükleme */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Toplu İçe Aktarma</h3>
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
              >
                <Download className="w-4 h-4" />
                Şablon İndir
              </button>
            </div>
            
            <div className="flex gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors border-2 border-dashed border-gray-600 hover:border-yellow-400"
              >
                <FileText className="w-5 h-5" />
                <span>CSV Dosyası Seçin veya Sürükleyin</span>
              </label>
            </div>
            
            <p className="mt-2 text-sm text-gray-400">
              CSV dosyası tek sütunda ilçe adlarını içermelidir.
            </p>
          </div>

          {/* İlçe Listesi */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-300">
                İlçe İsimleri
              </label>
              <span className="text-sm text-gray-400">
                {newDistricts.filter(d => d.trim() !== '').length} ilçe
              </span>
            </div>
            
            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
              {newDistricts.map((district, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={district}
                    onChange={(e) => handleDistrictChange(index, e.target.value)}
                    placeholder="İlçe adı"
                    className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                  {newDistricts.length > 1 && (
                    <button
                      onClick={() => handleRemoveDistrict(index)}
                      className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Yeni İlçe Ekleme Butonu */}
          <button
            onClick={handleAddDistrict}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Yeni İlçe Ekle
          </button>
        </div>

        {/* Hata ve Başarı Mesajları */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3 text-green-500">
            {success}
          </div>
        )}

        {/* Kaydet Butonu */}
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Kaydediliyor...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Kaydet</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}